// Lightweight markdown helpers shared across report components.
// Kept dep-free on purpose — these strings come from the backend memo and we
// want predictable, audit-able rendering (no surprise HTML escapes).

import { Fragment } from "react";

// ---------------------------------------------------------------------------
// Inline: only **bold** segments. Used by callouts where we don't want to
// over-format short blurbs (Plain English conclusion, Findings bullets).
// ---------------------------------------------------------------------------
export function renderMdInline(text) {
  if (!text) return null;
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((seg, i) => {
    if (seg.startsWith("**") && seg.endsWith("**")) {
      return (
        <strong key={i} className="font-medium text-[#1F382E]">
          {seg.slice(2, -2)}
        </strong>
      );
    }
    return <Fragment key={i}>{seg}</Fragment>;
  });
}

// ---------------------------------------------------------------------------
// Inline (full): **bold**, *italic*, `code`. Used inside the AI memo body where
// the LLM may emit any of these.
// ---------------------------------------------------------------------------
export function renderInline(text) {
  const parts = [];
  let remaining = text;
  let key = 0;
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/;
  while (remaining.length) {
    const match = remaining.match(pattern);
    if (!match) {
      parts.push(remaining);
      break;
    }
    const idx = match.index;
    if (idx > 0) parts.push(remaining.slice(0, idx));
    const tok = match[0];
    if (tok.startsWith("**"))
      parts.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith("`"))
      parts.push(
        <code
          key={key++}
          className="bg-[#F7F5F0] px-1.5 py-0.5 rounded text-sm"
        >
          {tok.slice(1, -1)}
        </code>
      );
    else parts.push(<em key={key++}>{tok.slice(1, -1)}</em>);
    remaining = remaining.slice(idx + tok.length);
  }
  return parts;
}

// ---------------------------------------------------------------------------
// Block: paragraphs, ## / ### headings, bullet + numbered lists, **Heading**
// lines. Used by the full CFO memo body.
// ---------------------------------------------------------------------------
export function Markdownish({ text }) {
  const lines = (text || "").split(/\r?\n/);
  const blocks = [];
  let buffer = [];
  let listType = null;

  function flushParagraph() {
    if (buffer.length) {
      blocks.push({ type: "p", text: buffer.join(" ") });
      buffer = [];
    }
  }
  function flushList() {
    if (listType && buffer.length) {
      blocks.push({ type: listType, items: [...buffer] });
      buffer = [];
      listType = null;
    }
  }

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      flushParagraph();
      flushList();
      continue;
    }
    if (line.startsWith("### ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h3", text: line.slice(4) });
      continue;
    }
    if (line.startsWith("## ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h2", text: line.slice(3) });
      continue;
    }
    if (line.startsWith("# ")) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h2", text: line.slice(2) });
      continue;
    }
    if (/^\*\*[^*]+\*\*\s*$/.test(line)) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h3", text: line.replace(/^\*\*|\*\*$/g, "") });
      continue;
    }
    if (/^[-*•]\s+/.test(line)) {
      if (listType !== "ul") {
        flushParagraph();
        flushList();
        listType = "ul";
      }
      buffer.push(line.replace(/^[-*•]\s+/, ""));
      continue;
    }
    if (/^\d+[.)]\s+/.test(line)) {
      if (listType !== "ol") {
        flushParagraph();
        flushList();
        listType = "ol";
      }
      buffer.push(line.replace(/^\d+[.)]\s+/, ""));
      continue;
    }
    if (listType) flushList();
    buffer.push(line);
  }
  flushParagraph();
  flushList();

  return (
    <div className="space-y-4 leading-relaxed text-[#1A1A1A]">
      {blocks.map((b, i) => {
        if (b.type === "h2")
          return (
            <h3
              key={i}
              className="font-serif-display text-2xl text-[#1F382E] mt-6 first:mt-0"
            >
              {renderInline(b.text)}
            </h3>
          );
        if (b.type === "h3")
          return (
            <h4
              key={i}
              className="font-serif-display text-xl text-[#1F382E] mt-4 first:mt-0"
            >
              {renderInline(b.text)}
            </h4>
          );
        if (b.type === "ul")
          return (
            <ul key={i} className="list-disc pl-5 space-y-1.5">
              {b.items.map((it, j) => (
                <li key={j}>{renderInline(it)}</li>
              ))}
            </ul>
          );
        if (b.type === "ol")
          return (
            <ol key={i} className="list-decimal pl-5 space-y-1.5">
              {b.items.map((it, j) => (
                <li key={j}>{renderInline(it)}</li>
              ))}
            </ol>
          );
        return <p key={i}>{renderInline(b.text)}</p>;
      })}
    </div>
  );
}
